import {
  type Ref,
  watch,
  onMounted,
  onBeforeUnmount,
  getCurrentInstance,
  inject,
  ref
} from 'vue'
import { createContext } from 'reka-ui'
import {
  type StakeholderContact,
  type StakeholderType,
  type ExtractionTarget,
  type AiExtractDataResponse,
  type StakeholderContactType,
  type AiExtractDataRequestPayload
} from '@gat-ship/types'
import { appLoadingKey } from '@gat-ship/types/injection-keys'
import { globalUtils } from '@gat-ship/utils'
import { useStakeholderContactsSignalREvents } from '@gat-ship/signalr'
import { useAiApiCalls } from '@gat-ship/smart-paste'
import { AppError, ErrorCode } from '@gat-ship/error-handling'
import { useStakeholderApiCalls } from './useStakeholderApiCalls'
import {
  buildInitialContact,
  mapContactsToBatchPayload,
  sortContacts
} from '../model/contact-builder'

export type ContactModalParams = {
  mainActionType: 'add' | 'update' | 'view'
  stakeholderId: string
  stakeholderName: string
  stakeholderShortName: string
  stakeholderType: StakeholderType
  contactType: StakeholderContactType
  contact?: StakeholderContact
}

export type ContactModalMainActionType = 'add' | 'update' | 'view'

export type ContactPersonModals = {
  contact: Ref<{ showModal: (params: ContactModalParams) => void } | null>
}

/** Self-inject the app-loading registry (UI-free). */
function useLoading() {
  return getCurrentInstance() ? inject(appLoadingKey, undefined) : undefined
}

function defineContactState() {
  const _contact = ref<StakeholderContact>()

  const mainActionType = ref<ContactModalMainActionType>('add')

  function initializeContact(params: {
    contact: StakeholderContact | undefined
    contactType: StakeholderContactType
    stakeholderInfo: {
      stakeholderId: string
      stakeholderName: string
      stakeholderShortName: string
      stakeholderType: StakeholderType
    }
    mainActionType: ContactModalMainActionType
  }) {
    mainActionType.value = params.mainActionType
    _contact.value = buildInitialContact({
      contact: params.contact,
      contactType: params.contactType,
      stakeholderInfo: params.stakeholderInfo
    })
  }

  function setAiValues(payload: AiExtractDataResponse) {
    const contact = _contact.value
    if (!contact) {
      return
    }
    if ('contactType' in payload && payload.contactType) {
      contact.contactType = payload.contactType.value as 'person' | 'other'
    }
  }

  return {
    contact: _contact,
    mainActionType,
    initializeContact,
    setAiValues
  }
}

function defineContactsState(props: {
  isCreate: boolean
  stakeholderId: string
}) {
  const _contacts = ref<StakeholderContact[]>([])

  function setContacts(contacts: StakeholderContact[]) {
    _contacts.value = sortContacts(contacts)
  }

  const { getStakeholderContactsRequest } = useStakeholderApiCalls()

  const loading = useLoading()

  async function fetchContacts(stakeholderId: string) {
    const loadingIds = [`loading-contacts`]
    loading?.setLoadingId(loadingIds)
    try {
      const response = await getStakeholderContactsRequest({
        stakeholderId,
        queryParams: {
          page: 1,
          pageSize: 100
        }
      })
      if (response.contacts) {
        setContacts(response.contacts)
      }
    } finally {
      loading?.removeLoadingId(loadingIds)
    }
  }

  watch(
    () => props.stakeholderId,
    (newStakeholderId) => {
      if (!props.isCreate) {
        fetchContacts(newStakeholderId)
      }
    },
    {
      immediate: true
    }
  )

  const signalREvents = useStakeholderContactsSignalREvents({
    onContactAdded: (payload) => {
      if (payload.message.stakeholderId !== props.stakeholderId) {
        return
      }
      fetchContacts(props.stakeholderId)
    },
    onContactUpdated: (payload) => {
      if (payload.message.stakeholderId !== props.stakeholderId) {
        return
      }
      fetchContacts(props.stakeholderId)
    },
    onContactDeleted: (payload) => {
      if (payload.message.stakeholderId !== props.stakeholderId) {
        return
      }
      fetchContacts(props.stakeholderId)
    }
  })

  onMounted(() => {
    if (props.isCreate) {
      return
    }
    signalREvents.subscribeToContactsEvents()
  })

  onBeforeUnmount(() => {
    if (props.isCreate) {
      return
    }
    signalREvents.unsubscribeFromContactsEvents()
  })

  return {
    contacts: _contacts,
    fetchContacts,
    setContacts
  }
}

function defineContactsCommands(
  props: { isCreate: boolean; stakeholderId: string },
  contactsState: ReturnType<typeof defineContactsState>,
  contactState: ReturnType<typeof defineContactState>
) {
  const loading = useLoading()

  const { extractDataRequest } = useAiApiCalls()

  async function extractAiData(payload: AiExtractDataRequestPayload) {
    const loadingIds = ['smart-paste-extract']
    const target: ExtractionTarget = 'DE:Stakeholders.V1.CreateContact'
    try {
      loading?.setLoadingId(loadingIds)
      const response = await extractDataRequest({
        ...payload,
        target
      })
      if (!response || !response.data) {
        throw new AppError({
          message: 'No data extracted',
          code: ErrorCode.UNEXPECTED
        })
      }
      if (response.data) {
        contactState.setAiValues(response.data)
      }
    } finally {
      loading?.removeLoadingId(loadingIds)
    }
  }

  const {
    addStakeholderContactRequest,
    addMultipleStakeholderContactsRequest,
    updateStakeholderContactRequest,
    deleteStakeholderContactRequest
  } = useStakeholderApiCalls()

  async function addContact(contact: StakeholderContact) {
    if (props.isCreate) {
      contactsState.setContacts([contact, ...contactsState.contacts.value])
      return
    }
    loading?.setLoadingId(contact.id)
    try {
      const response = await addStakeholderContactRequest({
        payload: contact,
        commandId: globalUtils.functions.uuid()
      })
      contactsState.setContacts([
        response.newState,
        ...contactsState.contacts.value
      ])
    } finally {
      loading?.removeLoadingId(contact.id)
    }
  }

  async function addMultipleContacts(contacts: StakeholderContact[]) {
    const payload = mapContactsToBatchPayload(props.stakeholderId, contacts)
    await addMultipleStakeholderContactsRequest({
      payload: payload,
      commandId: globalUtils.functions.uuid()
    })
    contactsState.fetchContacts(props.stakeholderId)
  }

  async function updateContact(contact: StakeholderContact) {
    if (props.isCreate) {
      contactsState.setContacts(
        contactsState.contacts.value.map((c) =>
          c.id === contact.id ? contact : c
        )
      )
      return
    }

    loading?.setLoadingId(contact.id)
    try {
      const response = await updateStakeholderContactRequest({
        contactId: contact.id,
        payload: contact,
        commandId: globalUtils.functions.uuid()
      })
      contactsState.setContacts(
        contactsState.contacts.value.map((c) =>
          c.id === contact.id ? response.newState : c
        )
      )
    } finally {
      loading?.removeLoadingId(contact.id)
    }
  }

  const showDeleteConfirm = ref(false)
  const pendingDeleteContact = ref<StakeholderContact>()

  function confirmDeleteContact(contact: StakeholderContact) {
    pendingDeleteContact.value = contact
    showDeleteConfirm.value = true
  }

  function onConfirmDelete() {
    showDeleteConfirm.value = false
    if (pendingDeleteContact.value) {
      deleteContact(pendingDeleteContact.value)
    }
  }

  async function deleteContact(contact: StakeholderContact) {
    const contactId = contact.id
    if (props.isCreate) {
      contactsState.setContacts(
        contactsState.contacts.value.filter((c) => c.id !== contactId)
      )
      return
    }
    loading?.setLoadingId(contactId)
    try {
      await deleteStakeholderContactRequest({
        contactId,
        commandId: globalUtils.functions.uuid()
      })
      contactsState.setContacts(
        contactsState.contacts.value.filter((c) => c.id !== contactId)
      )
    } finally {
      loading?.removeLoadingId(contactId)
    }
  }
  return {
    deleteContact: confirmDeleteContact,
    onConfirmDelete,
    showDeleteConfirm,
    pendingDeleteContact,
    updateContact,
    addContact,
    addMultipleContacts,
    extractAiData
  }
}

function defineContactsModals(modalRefs: ContactPersonModals) {
  function showViewContactModal(
    params: Omit<ContactModalParams, 'mainActionType'>
  ) {
    modalRefs.contact.value?.showModal({
      mainActionType: 'view',
      ...params
    })
  }
  function showAddContactModal(
    params: Omit<ContactModalParams, 'mainActionType'>
  ) {
    modalRefs.contact.value?.showModal({
      mainActionType: 'add',
      ...params
    })
  }

  function showEditContactModal(
    params: Omit<ContactModalParams, 'mainActionType'>
  ) {
    modalRefs.contact.value?.showModal({
      mainActionType: 'update',
      ...params
    })
  }

  return {
    showViewContactModal,
    showEditContactModal,
    showAddContactModal
  }
}

export function defineContactsContext(
  props: { isCreate: boolean; stakeholderId: string },
  modalRefs: ContactPersonModals
) {
  const contactsState = defineContactsState(props)
  const contactState = defineContactState()
  const commands = defineContactsCommands(props, contactsState, contactState)
  const modals = defineContactsModals(modalRefs)
  return {
    contacts: contactsState.contacts,
    setContacts: contactsState.setContacts,
    contact: contactState.contact,
    contactMainActionType: contactState.mainActionType,
    initializeContact: contactState.initializeContact,
    commands,
    modals
  }
}

export type ContactPersonContext = ReturnType<typeof defineContactsContext>

export const [useContactProvider, provideContactPerson] =
  createContext<ContactPersonContext>('stakeholder-contact-person')
